import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { UUID_RE } from "@/utils/generic";

// The endpoint returns a loosely-typed dict rather than a Pydantic response
// model, so the shape is narrowed here once instead of at every caller.
export type DeploymentApp = {
  deployment_id: string;
  assistant_id: string;
  assistant_name: string | null;
  assistant_image_url: string | null;
  channel_type: string;
  config: Record<string, unknown>;
  group_id: string;
  assistant_version_id: string | null;
};

/**
 * Resolve a deployment from a route segment that is either its id or its stable
 * alias — uuid-shaped aliases are rejected server-side, so the shape
 * discriminates which endpoint answers.
 *
 * Callers get `deploymentId` already resolved to the real id: the alias stops
 * at resolution, and passing one downstream (a conversation's `deployment_id`,
 * say) stores a reference the rest of the platform can't follow.
 */
export function useDeploymentApp(ref: string | undefined) {
  const isIdRef = !!ref && UUID_RE.test(ref);

  const byId = useRemoteData(
    DATA_KEYS.DEPLOYMENT_APP,
    { path: { deployment_id: ref || "" } },
    { enabled: !!ref && isIdRef }
  );
  const byAlias = useRemoteData(
    DATA_KEYS.DEPLOYMENT_APP_BY_ALIAS,
    { path: { alias: ref || "" } },
    { enabled: !!ref && !isIdRef }
  );

  const { data, isLoading, isError } = isIdRef ? byId : byAlias;
  const deploymentApp = data as DeploymentApp | undefined;

  return {
    deploymentApp,
    deploymentId: deploymentApp?.deployment_id ?? (isIdRef ? ref : undefined),
    isLoading,
    isError,
  };
}
